import { useState, useEffect, useCallback, useRef } from 'react'
import { Bell } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { BottomSheet } from '@/components/ui/BottomSheet'
import { useIsMobile } from '@/hooks/useIsMobile'
import { inboxTarget, useNotificationInbox, type InboxItem } from '@/hooks/useNotificationInbox'
import { QueryState } from '@/components/ui/QueryState'
import { ListSkeleton } from '@/components/ui/Skeletons'
import { NotificationList } from './NotificationList'
import { usePresence } from '@/hooks/usePresence'

// ─── The bell (UX-192) ────────────────────────────────────────────────────────
// Fed from the user's own notifications (new lead, quote approved, payment,
// overdue invoice, missed call, job events) — not the activity feed. Unread
// count from the server, "Mark all read", "See all" → /notifications, and a
// tap opens the thing the notification is about. Phone: a <BottomSheet>.

const PREVIEW = 8

export function NotificationDropdown({
  align = 'right',
  className,
}: {
  /** Which edge the panel lines up with. The sidebar bell opens to the right
   *  ('left' edge), the phone header bell to the left ('right' edge). */
  align?: 'left' | 'right'
  className?: string
} = {}) {
  const [open, setOpen] = useState(false)
  const panel = usePresence(open, 160)
  const inbox = useNotificationInbox({ limit: PREVIEW })
  const containerRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const { refetch } = inbox

  const handleOpen = useCallback(() => {
    setOpen(true)
    void refetch()
  }, [refetch])

  const handleClose = useCallback(() => setOpen(false), [])

  // Click-outside to close the desktop popover — no full-screen backdrop so
  // the rest of the shell stays interactive. The phone sheet has its own.
  useEffect(() => {
    if (!open || isMobile) return
    const handleOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) handleClose()
    }
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') handleClose() }
    document.addEventListener('mousedown', handleOutside)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleOutside)
      document.removeEventListener('keydown', handleKey)
    }
  }, [open, isMobile, handleClose])

  const openItem = (item: InboxItem) => {
    void inbox.markRead(item.id)
    handleClose()
    const to = inboxTarget(item)
    if (to) navigate(to)
  }

  const seeAll = () => {
    handleClose()
    navigate('/notifications')
  }

  const unread = inbox.unreadCount
  const label = unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'

  const body = (
    <QueryState
      query={inbox.query}
      action="load notifications"
      compact
      skeleton={<ListSkeleton rows={4} />}
      isEmpty={(items) => items.length === 0}
      empty={
        <div className="px-3 py-6 text-center">
          <p className="text-sm text-white/80">No notifications yet</p>
          <p className="text-[13px] text-white/[0.62] mt-1">New leads, payments and missed calls show up here.</p>
        </div>
      }
    >
      {(items) => <NotificationList items={items} onOpen={openItem} />}
    </QueryState>
  )

  const markAll = unread > 0 && (
    <button
      type="button"
      onClick={() => void inbox.markAllRead()}
      className="text-xs font-medium text-white/70 hover:text-white transition-colors px-2 py-1 rounded-full"
    >
      Mark all read
    </button>
  )

  const footer = (
    <button
      type="button"
      onClick={seeAll}
      className="w-full text-center text-sm font-medium text-white hover:bg-white/[0.04] rounded-md py-2.5 transition-colors"
    >
      See all notifications
    </button>
  )

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={() => (open ? handleClose() : handleOpen())}
        className={className ?? 'relative w-11 h-11 flex items-center justify-center text-ink-300 hover:text-ink-50 hover:bg-white/[0.04] rounded-full transition-all'}
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span
            data-testid="bell-unread"
            className="absolute top-2 right-2 w-2 h-2 bg-peach-200 rounded-full shadow-[0_0_6px_rgba(255,162,105,0.7)]"
          />
        )}
      </button>

      {isMobile ? (
        <BottomSheet
          open={open}
          onClose={handleClose}
          title={
            <span className="flex items-center justify-between gap-3 w-full">
              <span>Notifications</span>
              {markAll}
            </span>
          }
          footer={footer}
          snapPoints={['half', 'full']}
        >
          {body}
        </BottomSheet>
      ) : (
        panel.mounted && (
          // The same popover timing as every menu, in and out (UX-117: it
          // used to pop in and vanish in one frame).
          <div
            role="dialog"
            aria-label="Notifications"
            aria-hidden={panel.leaving || undefined}
            className={`absolute ${align === 'left' ? 'left-0 origin-top-left' : 'right-0 origin-top-right'} top-full mt-2 w-[22rem] glass-popup p-1 z-[41] ${panel.leaving ? 'animate-popover-out pointer-events-none' : 'animate-popover-in'}`}
          >
            <div className="px-3 py-2 hairline-b mb-1 flex items-center justify-between gap-2">
              <p className="text-sm font-medium text-ink-100">
                Notifications{unread > 0 ? <span className="text-white/[0.62] font-normal"> · {unread} new</span> : null}
              </p>
              {markAll}
            </div>
            <div className="max-h-96 overflow-y-auto overscroll-contain">{body}</div>
            <div className="hairline-t mt-1 pt-1">{footer}</div>
          </div>
        )
      )}
    </div>
  )
}
